/**
 * Planning Mode - Pre-flight analysis for AI agent operations
 * Provides intelligent analysis before execution
 */

import chalk from 'chalk';
import { existsSync, readdirSync } from 'fs';
import { join, extname } from 'path';

export interface ProjectAnalysis {
    type: ProjectType;
    files: string[];
    risks: Risk[];
    estimatedTime: number;
    confidence: number;
}

export interface Risk {
    level: 'low' | 'medium' | 'high';
    description: string;
    mitigation?: string;
}

export interface PlanStep {
    id: string;
    action: string;
    files: string[];
    risk: 'low' | 'medium' | 'high';
    estimatedDuration: number;
}

export interface Plan {
    analysis: ProjectAnalysis;
    steps: PlanStep[];
    totalEstimatedTime: number;
    requiresConfirmation: boolean;
}

export type ProjectType = 'node' | 'python' | 'go' | 'rust' | 'unknown';

export function detectProjectType(cwd: string = process.cwd()): ProjectType {
    const indicators = {
        'node': ['package.json', 'node_modules', 'yarn.lock', 'package-lock.json'],
        'python': ['requirements.txt', 'pyproject.toml', 'setup.py', 'Pipfile'],
        'go': ['go.mod', 'go.sum', 'main.go'],
        'rust': ['Cargo.toml', 'src/main.rs', 'Cargo.lock']
    };

    for (const [type, files] of Object.entries(indicators)) {
        if (files.some(file => existsSync(join(cwd, file)))) {
            return type as ProjectType;
        }
    }

    return 'unknown';
}

export function analyzeProject(cwd: string = process.cwd()): ProjectAnalysis {
    const projectType = detectProjectType(cwd);
    const files = getRelevantFiles(cwd, projectType);
    const risks = detectRisks(files, projectType);
    
    return {
        type: projectType,
        files,
        risks,
        estimatedTime: calculateEstimatedTime(files, projectType),
        confidence: calculateConfidence(files, projectType)
    };
}

function getRelevantFiles(cwd: string, projectType: ProjectType): string[] {
    const relevantExtensions: Record<ProjectType, Set<string>> = {
        'node': new Set(['.ts', '.js', '.tsx', '.jsx', '.json']),
        'python': new Set(['.py', '.txt', '.toml', '.cfg']),
        'go': new Set(['.go', '.mod']),
        'rust': new Set(['.rs', '.toml']),
        'unknown': new Set(['.md', '.txt', '.json', '.yaml', '.yml']),
    };

    const relevantNames: Record<ProjectType, Set<string>> = {
        'node': new Set(['package.json', 'tsconfig.json']),
        'python': new Set(['requirements.txt', 'pyproject.toml', 'setup.py', 'Pipfile']),
        'go': new Set(['go.mod']),
        'rust': new Set(['Cargo.toml']),
        'unknown': new Set([]),
    };

    const extensions = relevantExtensions[projectType] || relevantExtensions.unknown;
    const names = relevantNames[projectType] || relevantNames.unknown;
    const files: string[] = [];
    const MAX_FILES = 50;
    const MAX_DEPTH = 4;

    // Directories to skip for performance
    const SKIP_DIRS = new Set(['node_modules', '.git', 'dist', 'build', '__pycache__', '.next', 'target', 'vendor']);

    function walk(dir: string, depth: number): void {
        if (depth > MAX_DEPTH || files.length >= MAX_FILES) return;

        try {
            const entries = readdirSync(dir, { withFileTypes: true });
            for (const entry of entries) {
                if (files.length >= MAX_FILES) return;

                if (entry.isDirectory()) {
                    if (!SKIP_DIRS.has(entry.name) && !entry.name.startsWith('.')) {
                        walk(join(dir, entry.name), depth + 1);
                    }
                } else if (entry.isFile()) {
                    const ext = extname(entry.name);
                    if (extensions.has(ext) || names.has(entry.name)) {
                        files.push(join(dir, entry.name));
                    }
                }
            }
        } catch {
            // Permission denied or other FS errors - skip silently
        }
    }

    walk(cwd, 0);
    return files;
}

function detectRisks(files: string[], projectType: ProjectType): Risk[] {
    const risks: Risk[] = [];

    if (files.length > 100) {
        risks.push({
            level: 'high',
            description: 'Large project with many files',
            mitigation: 'Consider focusing on specific directories or files'
        });
    }

    if (projectType === 'unknown') {
        risks.push({
            level: 'medium',
            description: 'Unrecognized project type',
            mitigation: 'AI may have limited context for this project structure'
        });
    }

    const configFiles = files.filter(f => f.includes('config') || f.includes('.env'));
    if (configFiles.length === 0) {
        risks.push({
            level: 'low',
            description: 'No configuration files detected',
            mitigation: 'Project may need configuration for optimal analysis'
        });
    }

    return risks;
}

function calculateEstimatedTime(files: string[], projectType: ProjectType): number {
    const baseTime = 5;
    const fileComplexity = {
        'node': 0.1,
        'python': 0.12,
        'go': 0.08,
        'rust': 0.15,
        'unknown': 0.05
    };

    return baseTime + (files.length * (fileComplexity[projectType] || 0.1));
}

function calculateConfidence(files: string[], projectType: ProjectType): number {
    let confidence = 0.7;

    if (projectType !== 'unknown') confidence += 0.2;
    if (files.length > 0) confidence += 0.1;
    if (files.some(f => f.includes('README'))) confidence += 0.05;

    return Math.min(confidence, 1.0);
}

export function createPlan(analysis: ProjectAnalysis, userRequest: string): Plan {
    const steps = generatePlanSteps(analysis, userRequest);
    const totalTime = steps.reduce((sum, step) => sum + step.estimatedDuration, 0);
    const requiresConfirmation = analysis.risks.some(risk => risk.level === 'high') || totalTime > 60;

    return {
        analysis,
        steps,
        totalEstimatedTime: totalTime,
        requiresConfirmation
    };
}

function generatePlanSteps(analysis: ProjectAnalysis, userRequest: string): PlanStep[] {
    const steps: PlanStep[] = [];
    const { files, risks } = analysis;

    steps.push({
        id: 'analyze_context',
        action: 'Analyze project context and relevant files',
        files: files.slice(0, 10),
        risk: 'low',
        estimatedDuration: 5
    });

    if (userRequest.toLowerCase().includes('test') || userRequest.toLowerCase().includes('spec')) {
        steps.push({
            id: 'create_tests',
            action: 'Generate or modify test files',
            files: files.filter(f => f.includes('test') || f.includes('spec')),
            risk: 'medium',
            estimatedDuration: 15
        });
    }

    if (userRequest.toLowerCase().includes('feature') || userRequest.toLowerCase().includes('add')) {
        steps.push({
            id: 'implement_feature',
            action: 'Implement new functionality',
            files: files.filter(f => f.endsWith('.ts') || f.endsWith('.js') || f.endsWith('.py')),
            risk: 'medium',
            estimatedDuration: 20
        });
    }

    if (risks.some(r => r.level === 'high')) {
        steps.push({
            id: 'risk_mitigation',
            action: 'Apply risk mitigation strategies',
            files: [],
            risk: 'low',
            estimatedDuration: 5
        });
    }

    steps.push({
        id: 'validate_changes',
        action: 'Validate and review changes',
        files: files.slice(0, 5),
        risk: 'low',
        estimatedDuration: 3
    });

    return steps;
}

export function formatPlan(plan: Plan): string {
    const { analysis, steps, totalEstimatedTime, requiresConfirmation } = plan;
    
    let output = [
        chalk.bold.blue('\n📋 Execution Plan'),
        chalk.dim(`Project Type: ${analysis.type}`),
        chalk.dim(`Files to Analyze: ${analysis.files.length}`),
        chalk.dim(`Estimated Time: ${totalEstimatedTime}s`),
        ''
    ].join('\n');

    if (analysis.risks.length > 0) {
        output += chalk.bold.yellow('⚠️  Risks Detected:\n');
        analysis.risks.forEach(risk => {
            const icon = risk.level === 'high' ? '🔴' : risk.level === 'medium' ? '🟡' : '🟢';
            output += `  ${icon} ${risk.description}`;
            if (risk.mitigation) {
                output += chalk.dim(`\n     Mitigation: ${risk.mitigation}`);
            }
            output += '\n';
        });
        output += '\n';
    }

    output += chalk.bold.cyan('📝 Planned Steps:\n');
    steps.forEach((step, index) => {
        const riskIcon = step.risk === 'high' ? '🔴' : step.risk === 'medium' ? '🟡' : '🟢';
        output += `  ${index + 1}. ${step.action} (${step.estimatedDuration}s) ${riskIcon}\n`;
        if (step.files.length > 0) {
            output += chalk.dim(`     Files: ${step.files.slice(0, 3).join(', ')}${step.files.length > 3 ? '...' : ''}\n`);
        }
    });

    if (requiresConfirmation) {
        output += '\n' + chalk.bold.yellow('⚠️  This plan requires confirmation due to high-risk operations or long execution time.\n');
    }

    return output;
}